/* Billy cart: slide-out cart, in-place cart updates, suggested products and the dispatch cut-off.
   - Any native add-to-cart form on the site is sent with the Cart AJAX API; the slide-out cart then
     re-renders (Section Rendering API) and opens. The bundle buy box (billy-product.js) calls
     window.BillyCart.added(response) itself.
   - Quantity / remove links and suggestion "Add" buttons update the cart in place.
   - Cart changes made by other scripts (e.g. a free-gift app adding its gift) are detected and the
     slide-out cart / cart page refresh themselves.
   - Everything falls back to normal page loads without JavaScript. */
(() => {
  const nativeFetch = window.fetch.bind(window);
  const drawer = document.querySelector('[data-billy-drawer]');
  const onCartPage = drawer ? drawer.dataset.cartTemplate === 'true' : /\/cart\/?$/.test(location.pathname);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let lastFocus = null;

  const bodies = () => Array.from(document.querySelectorAll('[data-billy-cart-body]'));
  const sectionIds = () => [...new Set(bodies().map((b) => b.dataset.sectionId).filter(Boolean))];

  // ----- Drawer -----
  const open = () => {
    if (!drawer || onCartPage) return false;
    lastFocus = document.activeElement;
    drawer.removeAttribute('inert');
    drawer.classList.add('is-open');
    document.documentElement.classList.add('billy-lock');
    const panel = drawer.querySelector('.billy-drawer__panel');
    if (panel) setTimeout(() => panel.focus({ preventScroll: true }), 50);
    return true;
  };
  const close = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('inert', '');
    document.documentElement.classList.remove('billy-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };
  if (drawer) {
    drawer.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
  }
  const cartLink = document.getElementById('billy-cart-link');
  if (cartLink && drawer && !onCartPage) {
    cartLink.addEventListener('click', (e) => { e.preventDefault(); open(); });
  }

  // ----- Header count -----
  const setCount = (count) => {
    if (cartLink) {
      let badge = cartLink.querySelector('.billy-header__count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'billy-header__count';
          badge.setAttribute('aria-hidden', 'true');
          cartLink.appendChild(badge);
        }
        badge.textContent = count;
      } else if (badge) badge.remove();
      const label = cartLink.querySelector('.billy-visually-hidden');
      if (label) label.textContent = `Cart, ${count} ${count === 1 ? 'item' : 'items'}`;
    }
    const dc = drawer && drawer.querySelector('[data-drawer-count]');
    if (dc) dc.textContent = count > 0 ? `(${count})` : '';
  };

  // ----- Render returned sections -----
  const render = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-billy-cart-body]');
      if (!fresh) return;
      bodies().filter((b) => b.dataset.sectionId === id).forEach((b) => {
        const node = fresh.cloneNode(true);
        b.replaceWith(node);
        setup(node);
        setCount(Number(node.dataset.count) || 0);
      });
    });
  };

  const refresh = async () => {
    const ids = sectionIds();
    if (!ids.length) return;
    const res = await nativeFetch(`${location.pathname}?sections=${ids.join(',')}`);
    if (res.ok) render(await res.json());
  };

  // ----- Suggestions: show a random selection -----
  const shuffleUpsells = (scope) => {
    scope.querySelectorAll('[data-upsell-list]').forEach((list) => {
      const items = Array.from(list.children);
      for (let i = items.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
      }
      const limit = Number(list.dataset.limit) || 2;
      items.forEach((li, i) => { li.classList.toggle('is-extra', i >= limit); list.appendChild(li); });
      list.classList.add('is-shuffled');
    });
  };

  // ----- Dispatch cut-off (Brisbane time, no daylight saving; business days Mon-Fri) -----
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const hourLabel = (h) => `${((h + 11) % 12) + 1}${h < 12 ? 'am' : 'pm'}`;
  const cutoffTimers = new Set();
  const updateCutoff = (el) => {
    const hour = Number(el.dataset.hour) || 20;
    const now = Date.now();
    const bne = new Date(now + 10 * 3600000); // read with getUTC* = Brisbane wall clock
    const dayStart = Date.UTC(bne.getUTCFullYear(), bne.getUTCMonth(), bne.getUTCDate());
    const isBiz = (ms) => { const d = new Date(ms).getUTCDay(); return d > 0 && d < 6; };
    const DAY = 86400000;
    const nextBiz = (ms) => { let t = ms + DAY; while (!isBiz(t)) t += DAY; return t; };

    let cutoffDay = dayStart;
    if (!isBiz(dayStart) || bne.getUTCHours() >= hour) cutoffDay = nextBiz(dayStart);
    const dispatchDay = nextBiz(cutoffDay);
    const daysAway = (ms) => Math.round((ms - dayStart) / DAY);

    let when = hourLabel(hour);
    if (daysAway(cutoffDay) === 1) when += ' tomorrow';
    else if (daysAway(cutoffDay) > 1) when += ` ${DAYS[new Date(cutoffDay).getUTCDay()]}`;
    const ship = daysAway(dispatchDay) === 1 ? 'tomorrow' : DAYS[new Date(dispatchDay).getUTCDay()];

    const msLeft = cutoffDay + hour * 3600000 - bne.getTime();
    const text = el.querySelector('[data-cutoff-text]');
    const left = el.querySelector('[data-cutoff-left]');
    if (text) text.innerHTML = `Order before <strong>${when}</strong> for dispatch <strong>${ship}</strong>`;
    if (left) {
      if (msLeft > 0 && msLeft < DAY) {
        const mins = Math.floor(msLeft / 60000);
        left.textContent = `${Math.floor(mins / 60)}h ${mins % 60}m left`;
        left.hidden = false;
      } else left.hidden = true;
    }
    el.hidden = false;
  };
  setInterval(() => cutoffTimers.forEach((el) => (el.isConnected ? updateCutoff(el) : cutoffTimers.delete(el))), 30000);

  const setup = (scope) => {
    shuffleUpsells(scope);
    scope.querySelectorAll('[data-billy-cutoff]').forEach((el) => { cutoffTimers.add(el); updateCutoff(el); });
  };
  bodies().forEach(setup);

  // ----- Errors -----
  const showError = (anchor, message) => {
    if (!anchor) return;
    let box = anchor.querySelector('[data-error]') || anchor.querySelector('.billy-form-error');
    if (!box) {
      box = document.createElement('p');
      box.className = 'billy-form-error';
      box.setAttribute('role', 'alert');
      anchor.appendChild(box);
    }
    box.textContent = message;
    box.hidden = false;
  };

  // ----- Add to cart (native product forms) -----
  const added = (data) => {
    if (data && data.sections) render(data.sections);
    if (!open() && onCartPage && !(data && data.sections)) refresh();
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add/.test(action) || e.defaultPrevented) return;
    e.preventDefault();

    const submitter = e.submitter || form.querySelector('[type="submit"]');
    if (submitter) { submitter.disabled = true; submitter.setAttribute('aria-busy', 'true'); }
    const old = form.querySelector('.billy-form-error');
    if (old) old.hidden = true;

    const body = new FormData(form);
    const ids = sectionIds();
    if (ids.length) { body.append('sections', ids.join(',')); body.append('sections_url', location.pathname); }
    try {
      const res = await nativeFetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
      added(data);
    } catch (err) {
      showError(form, err.message);
    } finally {
      if (submitter) { submitter.disabled = false; submitter.removeAttribute('aria-busy'); }
    }
  });

  // ----- Quantity / remove -----
  document.addEventListener('click', async (e) => {
    const link = e.target.closest('[data-cart-change]');
    if (!link) return;
    e.preventDefault();
    const body = link.closest('[data-billy-cart-body]');
    if (body) body.setAttribute('aria-busy', 'true');
    try {
      const res = await nativeFetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(link.dataset.line), quantity: Number(link.dataset.quantity), sections: sectionIds(), sections_url: location.pathname }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.description || data.message || 'Could not update your bag.');
      render(data.sections);
    } catch (err) {
      if (body) {
        body.removeAttribute('aria-busy');
        showError(body.querySelector('.billy-cart__main') || body, err.message);
      }
    }
  });

  // ----- Cart changes made by other scripts (free-gift apps etc.) -----
  // Our own requests use nativeFetch and never pass through here. Requests that already ask for the
  // cart sections (the bundle buy box) render themselves, so they are skipped too.
  const CART_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  let refreshTimer = null;
  const queueRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => { refresh().catch(() => {}); }, 250);
  };
  const asksForSections = (body) => typeof body === 'string' && body.indexOf('sections') !== -1;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : String((input && (input.url || input.href)) || '');
    const watched = CART_URL.test(url.split('#')[0]) && !asksForSections(init && init.body);
    const result = nativeFetch(input, init);
    if (watched) result.then((res) => { if (res.ok) queueRefresh(); }).catch(() => {});
    return result;
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.billyCartUrl = String(url || '');
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    if (CART_URL.test((this.billyCartUrl || '').split('#')[0]) && !asksForSections(body)) {
      this.addEventListener('load', () => { if (this.status >= 200 && this.status < 300) queueRefresh(); });
    }
    return xhrSend.apply(this, arguments);
  };

  window.BillyCart = { open, close, refresh, render, added, sectionIds };
})();
